import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@loot-os/ui/components/sidebar";
import { Toggle } from "@loot-os/ui/components/toggle";
import { ToggleGroup } from "@loot-os/ui/components/toggle-group";
import { type LucideIcon, Monitor, Moon, Sun } from "lucide-react";

import { parseThemeChoice, type ThemeChoice, useThemeStore } from "../lib/theme";

const THEME_OPTIONS: Record<ThemeChoice, { label: string; Icon: LucideIcon }> = {
  light: { label: "Light", Icon: Sun },
  system: { label: "System", Icon: Monitor },
  dark: { label: "Dark", Icon: Moon },
};

/** The order the segments and menu items show in. */
const THEME_ORDER: ThemeChoice[] = ["light", "system", "dark"];

/**
 * The Light / System / Dark setting for the sidebar footer: three segments while the sidebar is
 * expanded, and one button with the current choice's icon and a menu while it is collapsed to
 * icons.
 */
export function ThemeToggle() {
  const choice = useThemeStore((s) => s.choice);
  const setChoice = useThemeStore((s) => s.setChoice);
  const { label: currentLabel, Icon: CurrentIcon } = THEME_OPTIONS[choice];

  return (
    <>
      <ToggleGroup
        className="w-full group-data-[collapsible=icon]:hidden"
        value={[choice]}
        onValueChange={(values) => {
          // Clicking the active segment sends an empty list; keep the current choice.
          if (values.length > 0) setChoice(parseThemeChoice(values[0]));
        }}
      >
        {THEME_ORDER.map((value) => {
          const { label, Icon } = THEME_OPTIONS[value];
          return (
            <Toggle key={value} value={value} size="sm" aria-label={label} className="flex-1">
              <Icon />
              {label}
            </Toggle>
          );
        })}
      </ToggleGroup>
      <SidebarMenu className="hidden group-data-[collapsible=icon]:flex">
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <SidebarMenuButton tooltip={`Theme: ${currentLabel}`}>
                  <CurrentIcon />
                  <span>Theme</span>
                </SidebarMenuButton>
              }
            />
            <DropdownMenuContent side="right" align="end" sideOffset={4} className="min-w-36">
              <DropdownMenuRadioGroup
                value={choice}
                onValueChange={(value) => setChoice(parseThemeChoice(value))}
              >
                {THEME_ORDER.map((value) => {
                  const { label, Icon } = THEME_OPTIONS[value];
                  return (
                    <DropdownMenuRadioItem key={value} value={value}>
                      <Icon />
                      {label}
                    </DropdownMenuRadioItem>
                  );
                })}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
